import { gql, useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { useShouldRemoveFileUploadPrefix } from '@modules/common/hooks';
import { replaceSourceNameForTenantIfRequired } from '@modules/common/utils';
import {
  EqlField,
  EqlFieldValue,
  IntegrationChannelType,
  Query,
  ResolvedEqlFilterValue,
  ResolveEqlReferencesInput,
  Source,
} from 'generated/graphql';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';

const FETCH_EQL_REFERENCES = gql`
  query resolveEQLReferences($tenantID: ID!, $eql: String!) {
    resolveEQLReferences(request: { tenantID: $tenantID, eql: $eql }) {
      filters {
        field {
          field
          displayName
          iconURL
          valueType
          objectType
          isMultiValue
          detail {
            metadata {
              source
              references {
                key
                displayName
                iconURL
                source
              }
            }
          }
        }
        valuesV2 {
          reference
          value {
            value
            displayName
            iconURL
          }
        }
      }
    }
  }
`;

export type EqlFieldByID = Record<
  string,
  | {
      field?: EqlField;
      eqlFieldValueByID?: Record<string, EqlFieldValue | undefined>;
    }
  | undefined
>;

interface useFetchEQLReferencesProps {
  whereClause: WhereClause;
  onCompleted?: (data: EqlFieldByID) => void;
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}

const getFieldIDToValueMap = (valuesV2: ResolvedEqlFilterValue[]) => {
  const fieldIDToValueMap: Record<string, EqlFieldValue> = {};

  valuesV2.forEach((valueV2) => {
    fieldIDToValueMap[valueV2.reference] = valueV2.value;
  });

  return fieldIDToValueMap;
};

export const useFetchEQLReferences = ({
  whereClause,
  onCompleted,
  fetchPolicy = 'cache-first',
  skip = false,
}: useFetchEQLReferencesProps) => {
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const [eqlFieldByID, setEqlFieldByID] = useState<EqlFieldByID>({});

  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { eql = '' } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const shouldSkipCall = skip || !eql;

  useEffect(() => {
    if (shouldSkipCall) {
      setEqlFieldByID({});
    }
  }, [shouldSkipCall]);

  const { loading } = useQuery<Query, ResolveEqlReferencesInput>(
    FETCH_EQL_REFERENCES,
    {
      fetchPolicy,
      nextFetchPolicy: 'cache-first',
      skip: shouldSkipCall,
      variables: {
        tenantID,
        eql,
      },
      onCompleted(data) {
        const filterValueMap: EqlFieldByID = {};

        data?.resolveEQLReferences?.filters.forEach((filter) => {
          const valuesV2 = filter.valuesV2.map((valueV2) => {
            let displayName = valueV2.value.displayName;

            if (
              valueV2.value?.value.startsWith(Source.FileUpload) &&
              shouldRemoveFileUploadPrefix
            ) {
              displayName = displayName
                .replaceAll(IntegrationChannelType.FileUpload, '')
                .trim();
            }

            return {
              ...valueV2,
              value: {
                ...valueV2.value,
                displayName: replaceSourceNameForTenantIfRequired(displayName),
              },
            };
          });

          filterValueMap[filter.field.field] = {
            field: filter.field,
            eqlFieldValueByID: {
              ...getFieldIDToValueMap(valuesV2),
            },
          };
        });

        if (!isEqual(filterValueMap, eqlFieldByID)) {
          setEqlFieldByID(filterValueMap);
          onCompleted?.(filterValueMap);
        }
      },
    },
  );

  return {
    eqlFieldByID,
    isLoading: loading,
  } as const;
};
